import React, { useRef, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { usePickemLeaderboard, useUserPickemPicks } from '@/api/query/useLeaderboard';
import EmptyState from '@/components/shared/EmptyState';
import { CountUpNumber } from '@/components/shared/CountUpNumber';
import { QuestionPickRow } from './QuestionPickRow';
import { pickemLeaderboardGridClass, DEFAULT_VISIBLE_CURRENCIES } from './currencyColumns';

// Prize object structure from API
interface PrizeData {
  cash: number | null;
  crowns: number | null;
  other: string | null;
  status: string;
}

// Type matching the actual API response
interface PickemLeaderboardEntry {
  user_id: string;
  username: string;
  favourite_team_id: string | null;
  points: number;
  rank: number;
  /** Distance from the correct tiebreaker answer; null when ungraded or no tiebreaker. */
  tiebreaker_margin?: string | null;
  prize?: PrizeData | null;
}

interface PickemLeaderboardProps {
  pickemId: string;
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
  /**
   * Contest start time. When provided and in the future, other users'
   * picks are hidden (chevron suppressed) so the leaderboard does not
   * spoil selections pre-kickoff. The current user can always view
   * their own picks.
   */
  contestStartUtc?: string | null;
}

// Component for expanded picks section
const ExpandedPicks: React.FC<{
  pickemId: string;
  userId: string;
  isCurrentUser: boolean;
  prize?: PrizeData | null;
}> = ({ pickemId, userId, isCurrentUser, prize }) => {
  const { data, isLoading, error } = useUserPickemPicks(pickemId, userId);

  if (isLoading) {
    return (
      <div
        className={`px-4 py-2 text-sm ${isCurrentUser ? 'text-black/70' : 'text-text-secondary'}`}
      >
        Loading picks...
      </div>
    );
  }

  if (error || !data?.picks || data.picks.length === 0) {
    return (
      <div
        className={`px-4 py-2 text-sm ${isCurrentUser ? 'text-black/70' : 'text-text-secondary'}`}
      >
        No picks available
      </div>
    );
  }

  // Check if there are additional prizes beyond cash (crowns or other) to show on mobile
  const hasAdditionalPrizes = prize && ((prize.crowns && prize.crowns > 0) || prize.other);

  return (
    <div className="px-4 pb-2 bg-app-surface-elevated">
      <div className="border-l-2 pl-2 border-border/30">
        {data.picks.map((pick, index) => (
          <QuestionPickRow
            key={pick.question_id}
            questionNumber={index + 1}
            questionText={pick.question_text}
            optionText={pick.option_text}
            customValue={pick.custom_value}
            optionImage={pick.option_image}
            isCorrect={pick.is_correct}
            pointsEarned={pick.points_earned}
            isCurrentUser={isCurrentUser}
          />
        ))}
      </div>

      {/* Prize details footer - visible on mobile when there are additional prizes */}
      {hasAdditionalPrizes && (
        <div
          className={`mt-3 pt-3 border-t sm:hidden ${isCurrentUser ? 'border-black/20' : 'border-border/30'}`}
        >
          <div
            className={`text-xs font-medium mb-1 ${isCurrentUser ? 'text-black/70' : 'text-text-secondary'}`}
          >
            Prize Details
          </div>
          <div className="flex flex-wrap gap-2">
            {prize.cash && prize.cash > 0 && (
              <span
                className={`text-sm font-bold ${isCurrentUser ? 'text-black' : 'text-green-500'}`}
              >
                ${prize.cash.toLocaleString()}
              </span>
            )}
            {prize.crowns && prize.crowns > 0 && (
              <span
                className={`text-sm font-medium ${isCurrentUser ? 'text-black/80' : 'text-yellow-500'}`}
              >
                {prize.crowns.toLocaleString()} Crowns
              </span>
            )}
            {prize.other && (
              <span
                className={`text-sm font-medium ${isCurrentUser ? 'text-black/80' : 'text-blue-400'}`}
              >
                {prize.other}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const PickemLeaderboard: React.FC<PickemLeaderboardProps> = ({
  pickemId,
  isOpen,
  onClose: _onClose,
  currentUserId,
  contestStartUtc,
}) => {
  // Other users' picks remain hidden until the contest has started.
  // Recompute on every tick after a forced re-render at kickoff so the UI
  // unlocks without requiring a manual refresh.
  const [now, setNow] = useState(() => Date.now());
  const startMs = contestStartUtc ? new Date(contestStartUtc).getTime() : null;
  const contestHasStarted = startMs === null || startMs <= now;

  useEffect(() => {
    if (startMs === null || startMs <= now) return;
    // Cap at the JS setTimeout safe range — beyond ~24.8 days timers fire
    // immediately, which would cause unnecessary re-renders for far-future
    // contests. Re-arm on next render in that case.
    const delay = Math.min(startMs - now, 2_147_483_000);
    const t = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(t);
  }, [startMs, now]);
  const prefersReducedMotion = useReducedMotion();
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePickemLeaderboard({
      pickemId,
    });
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Early return AFTER all hooks to avoid hook ordering issues
  if (!isOpen) return null;

  // Flatten all pages into single leaderboard array
  const leaderboard = data?.pages.flatMap(page => page.leaderboard) || [];

  // DKPLAT-618: only show Cash/Crowns columns when this pick'em's payout
  // template uses them (SkyPicks pays Other only). Grid tracks adapt to match.
  const currencies = data?.pages[0]?.visibleCurrencies ?? DEFAULT_VISIBLE_CURRENCIES;

  // DKPLAT-732: explain the ordering rather than leave players to infer it.
  //
  // Only shown once a tiebreaker has actually been graded — any entry with a margin proves
  // the contest has one. Before that, or on a contest without one, the note would be noise.
  //
  // The entry-time clause is conditional because it is only true of FIXED prizing; split
  // prizing lets equal-margin entries share a position instead. Claiming it either way
  // would be worse than saying nothing.
  const hasGradedTiebreaker = (data?.pages ?? []).some(page =>
    (page?.leaderboard ?? []).some(
      (entry: PickemLeaderboardEntry) => entry.tiebreaker_margin != null
    )
  );
  const entryTimeBreaksTies = data?.pages[0]?.entryTimeBreaksTies ?? false;
  const tiebreakNote = hasGradedTiebreaker
    ? entryTimeBreaksTies
      ? 'Level scores are separated by how close the tiebreaker answer was (±), then by who entered first.'
      : 'Level scores are separated by how close the tiebreaker answer was (±).'
    : null;
  const gridClass = pickemLeaderboardGridClass(currencies);

  // Find current user entry
  const currentUserEntry = currentUserId
    ? leaderboard.find((entry: PickemLeaderboardEntry) => entry.user_id === currentUserId)
    : undefined;

  // Helper function to render a leaderboard row
  const renderLeaderboardRow = (
    entry: PickemLeaderboardEntry,
    isCurrentUser: boolean,
    isPinned: boolean = false
  ) => {
    const canRevealPicks = isCurrentUser || contestHasStarted;
    const isExpanded = canRevealPicks && expandedUsers.has(entry.user_id);
    const rowKey = isPinned ? `${entry.user_id}-pinned` : entry.user_id;

    return (
      // `layout="position"` so an entry that changes rank slides to its new place as the
      // leaderboard re-sorts. Position only: the row also changes HEIGHT when its picks
      // expand below, and animating that would fight the expand.
      //
      // `layoutDependency` is what keeps expanding a row from glitching. `expandedUsers`
      // is state up here, so opening ONE row re-renders every row, and a bare `layout`
      // re-measures on each render — every row below would then animate down over the
      // picks that had just appeared instantly beneath it. Tied to the rank, a row only
      // measures when the thing we actually want to animate has changed.
      <motion.div
        key={rowKey}
        layout={prefersReducedMotion ? false : 'position'}
        layoutDependency={entry.rank}
        transition={{ layout: { duration: 0.3, ease: 'easeOut' } }}
      >
        <button
          onClick={() => canRevealPicks && toggleExpanded(entry.user_id)}
          disabled={!canRevealPicks}
          data-test-id={`leaderboard-row-${entry.rank}${isPinned ? '-pinned' : ''}`}
          data-test-state={isCurrentUser ? 'current-user' : 'other-user'}
          className={`w-full grid ${gridClass} gap-2 px-3 py-2 my-[1px] transition-smooth text-left ${
            isCurrentUser
              ? 'bg-pickem-primary text-black'
              : 'bg-app-surface-elevated hover:bg-game-card-active'
          }`}
        >
          {/* Rank Column */}
          <div
            className={`flex items-center justify-start font-bold ${isCurrentUser ? 'text-black' : 'text-text-primary'}`}
          >
            {entry.rank}
          </div>

          {/* User Column */}
          <div
            className={`flex items-center font-bold truncate ${isCurrentUser ? 'text-black' : 'text-text-primary'}`}
          >
            {entry.username}
          </div>

          {/* Cash Column - shown only when the currency is in use */}
          {currencies.cash && (
            <div className="flex items-center justify-start">
              {entry.prize?.cash && entry.prize.cash > 0 ? (
                <span
                  className={`font-bold text-sm ${isCurrentUser ? 'text-black' : 'text-green-500'}`}
                >
                  ${entry.prize.cash.toLocaleString()}
                </span>
              ) : (
                <span
                  className={`text-sm ${isCurrentUser ? 'text-black/40' : 'text-text-secondary/40'}`}
                >
                  -
                </span>
              )}
            </div>
          )}

          {/* Crowns Column - hidden on mobile, shown only when in use */}
          {currencies.crowns && (
            <div className="hidden sm:flex items-center justify-start">
              {entry.prize?.crowns && entry.prize.crowns > 0 ? (
                <span className={`text-sm ${isCurrentUser ? 'text-black/80' : 'text-yellow-500'}`}>
                  {entry.prize.crowns.toLocaleString()}c
                </span>
              ) : (
                <span
                  className={`text-sm ${isCurrentUser ? 'text-black/40' : 'text-text-secondary/40'}`}
                >
                  -
                </span>
              )}
            </div>
          )}

          {/* Other Column - hidden on mobile */}
          <div className="hidden sm:flex items-center justify-start">
            {entry.prize?.other ? (
              <span
                className={`text-sm truncate ${isCurrentUser ? 'text-black/80' : 'text-blue-400'}`}
                title={entry.prize.other}
              >
                {entry.prize.other}
              </span>
            ) : (
              <span
                className={`text-sm ${isCurrentUser ? 'text-black/40' : 'text-text-secondary/40'}`}
              >
                -
              </span>
            )}
          </div>

          {/* Points Column - always visible */}
          <div className="flex flex-col items-center justify-center leading-tight">
            <CountUpNumber
              className={`font-bold ${isCurrentUser ? 'text-black' : 'text-text-primary'}`}
              value={entry.points}
            />
            {/*
              Tiebreaker margin, when the contest has one (DKPLAT-732). Without it a screen
              of entries all on the same points gave no clue why they were ordered as they
              were — the ordering is correct, it was simply invisible.

              Stacked under the points rather than given its own column: the grid is shared
              with the currency columns and is already tight on a phone, and a new column
              would push the expand chevron off narrow screens.

              Rendered only when non-null, so contests without a tiebreaker look unchanged.
              The stored precision is kept so this agrees with the admin leaderboard rather
              than showing a player a differently-rounded number.
            */}
            {entry.tiebreaker_margin != null && (
              <span
                data-test-id="tiebreaker-margin"
                className={`text-[10px] ${isCurrentUser ? 'text-black/60' : 'text-text-secondary'}`}
              >
                ±{entry.tiebreaker_margin}
              </span>
            )}
          </div>

          {/* Expand/Collapse Icon — suppressed for other users until kickoff */}
          <div
            className={`flex items-center justify-center ${isCurrentUser ? 'text-black/70' : 'text-text-secondary'}`}
          >
            {canRevealPicks ? (
              isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )
            ) : null}
          </div>
        </button>

        {/* Expanded Picks Section */}
        {isExpanded && (
          <ExpandedPicks
            pickemId={pickemId}
            userId={entry.user_id}
            isCurrentUser={isCurrentUser}
            prize={entry.prize}
          />
        )}
      </motion.div>
    );
  };

  return (
    <div data-test-id="pickem-leaderboard-overlay" className="pt-4">
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="p-8 text-center">
              <div className="text-text-secondary">Loading leaderboard...</div>
            </div>
          )}

          {error && (
            <div className="p-8">
              <EmptyState title="Failed to load leaderboard" showAdSpace={false} />
            </div>
          )}

          {!isLoading && !error && leaderboard.length === 0 && (
            <div className="p-8">
              <EmptyState title="No leaderboard data available" showAdSpace={false} />
            </div>
          )}

          {!isLoading && !error && leaderboard.length > 0 && (
            <>
              {/* Table Header */}
              <div
                className={`grid ${gridClass} gap-2 px-3 py-3 bg-app-background border-b border-border/20`}
              >
                <div className="text-text-secondary font-medium text-sm">Rank</div>
                <div className="text-text-secondary font-medium text-sm">User</div>
                {currencies.cash && (
                  <div className="text-text-secondary font-medium text-sm">Cash</div>
                )}
                {currencies.crowns && (
                  <div className="hidden sm:block text-text-secondary font-medium text-sm">
                    Crowns
                  </div>
                )}
                <div className="hidden sm:block text-text-secondary font-medium text-sm">Other</div>
                <div className="text-text-secondary font-medium text-sm text-center">Pts</div>
                <div></div> {/* Spacer for expand icon */}
              </div>

              {tiebreakNote && (
                <div
                  data-test-id="tiebreak-note"
                  className="px-3 py-2 text-xs text-text-secondary bg-app-background border-b border-border/10"
                >
                  {tiebreakNote}
                </div>
              )}

              {/* Leaderboard Rows */}
              <div className="divide-y divide-border/10">
                {/* Current User Pinned at Top */}
                {currentUserEntry && (
                  <>
                    {renderLeaderboardRow(currentUserEntry, true, true)}
                    {/* Divider after pinned user */}
                    <div className="border-t border-border/20" />
                  </>
                )}

                {/* All Leaderboard Entries (including current user in their correct position) */}
                {leaderboard.length > 1 &&
                  leaderboard.map((entry: PickemLeaderboardEntry) => {
                    const isCurrentUser = currentUserId && entry.user_id === currentUserId;
                    return renderLeaderboardRow(entry, isCurrentUser, false);
                  })}
              </div>

              {/* Sentinel element for infinite scroll */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="py-4 text-center">
                  {isFetchingNextPage ? (
                    <div className="text-text-secondary text-sm">Loading more...</div>
                  ) : (
                    <div className="text-text-secondary text-sm">Scroll for more</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickemLeaderboard;
